import type { WidgetConfig } from './index.js';
import type { Analytics } from './analytics.js';
import type { ProductData, BrandData } from './SuppCoWidget.js';
import type { TrustCategory } from './trust-categories.js';

export interface ModalTrustCategory {
  name: string;
  score: number;
  description: string;
  className: string;
}

export class Modal {
  private config: WidgetConfig;
  private analytics: Analytics;
  private modalElement: HTMLElement | null = null;
  private iframeElement: HTMLIFrameElement | null = null;
  private currentProductIdentifier?: string;
  private trustCategories: TrustCategory[] = [];

  constructor(config: WidgetConfig, analytics: Analytics) {
    this.config = config;
    this.analytics = analytics;
    this.setupMessageListener();
  }

  private setupMessageListener(): void {
    window.addEventListener('message', (event) => {
      // Validate origin if in production
      if (!this.config.debug && event.origin !== this.config.domain) {
        return;
      }

      const { type, data } = event.data;

      switch (type) {
        case 'WIDGET_READY':
          this.handleWidgetReady(data);
          break;
        case 'SUPP_MODAL_RESIZE':
          this.handleIframeResize(data.height);
          break;
        case 'SUPP_MODAL_CLOSE':
          this.close();
          break;
        case 'SUPP_MODAL_ANALYTICS':
          this.analytics.trackClick('', data.event, data.properties);
          break;
      }
    });
  }

  private handleWidgetReady(data: any): void {
    console.log('🎯 Widget iframe ready:', data);
    
    // Send initialization data to widget if needed
    if (this.iframeElement) {
      const initMessage = {
        type: 'WIDGET_INIT',
        data: {
          parentOrigin: window.location.origin,
          timestamp: Date.now()
        }
      };
      
      this.iframeElement.contentWindow?.postMessage(initMessage, this.config.domain);
    }
  }

  private handleIframeResize(height: number): void {
    if (this.iframeElement && this.modalElement) {
      const wrapper = this.modalElement.querySelector('.supp-modal-iframe-wrapper') as HTMLElement;
      
      // Add smooth transition for resize
      this.iframeElement.style.transition = 'height 0.3s ease';
      this.iframeElement.style.height = `${height}px`;
      
      if (wrapper) {
        wrapper.style.transition = 'height 0.3s ease';
        wrapper.style.height = `${height}px`;
      }
      
      // Remove transition after resize to avoid interfering with other changes
      setTimeout(() => {
        if (this.iframeElement) {
          this.iframeElement.style.transition = '';
        }
        if (wrapper) {
          wrapper.style.transition = '';
        }
      }, 300);
    }
  }

  public open(
    productData: ProductData, 
    brandData: BrandData | null, 
    trustCategories: TrustCategory[],
    productIdentifier?: string,
    preloadedIframe?: HTMLIFrameElement | null
  ): void {
    console.log('🎯 Modal.open() called');
    this.currentProductIdentifier = productIdentifier;
    this.trustCategories = trustCategories;

    console.log('🎯 Opening iframe modal');
    this.openIframeModal(productData, brandData, trustCategories, preloadedIframe);
    console.log('🎯 Modal.open() completed');
  }

  private openIframeModal(
    productData: ProductData, 
    brandData: BrandData | null, 
    trustCategories: TrustCategory[],
    preloadedIframe?: HTMLIFrameElement | null
  ): void {
    console.log('🎬 openIframeModal started');
    this.createModalBackdrop();
    console.log('🎬 Modal backdrop created');
    
    if (preloadedIframe) {
      console.log('🔄 Using preloaded iframe:', preloadedIframe);
      this.usePreloadedIframe(preloadedIframe);
    } else {
      console.log('🔄 Creating new iframe (no preloaded iframe available)');
      this.createIframe(productData, brandData, trustCategories);
    }
    
    console.log('🎬 About to call showModal()');
    this.showModal();
    console.log('🎬 showModal() completed');
  }

  private createModalBackdrop(): void {
    console.log('🎬 createModalBackdrop() called');
    
    this.modalElement = document.createElement('div');
    this.modalElement.className = 'supp-modal-overlay';
    console.log('🎬 Modal element created with class:', this.modalElement.className);
    
    // For preloaded iframe, we just need backdrop + close button (iframe already positioned)
    this.modalElement.innerHTML = `
      <button class="supp-modal-close supp-modal-close-overlay" aria-label="Close modal">&times;</button>
    `;
    console.log('🎬 Modal backdrop HTML set');

    // Add styles for iframe modal
    this.addIframeModalStyles();
    console.log('🎬 Iframe modal styles added');

    // Close handlers
    const closeBtn = this.modalElement.querySelector('.supp-modal-close');
    closeBtn?.addEventListener('click', () => {
      console.log('🎬 Close button clicked');
      this.close();
    });
    
    this.modalElement.addEventListener('click', (e) => {
      if (e.target === this.modalElement) {
        console.log('🎬 Backdrop clicked');
        this.close();
      }
    });
    
    console.log('🎬 Adding backdrop to document.body');
    document.body.appendChild(this.modalElement);
    console.log('🎬 createModalBackdrop() completed');
  }

  private usePreloadedIframe(preloadedIframe: HTMLIFrameElement): void {
    console.log('📦 Current preloaded iframe src:', preloadedIframe.src);

    // Restore modal positioning (in case it was moved off-screen)
    preloadedIframe.className = 'supp-modal-iframe';
    preloadedIframe.style.position = 'fixed';
    preloadedIframe.style.top = '50%';
    preloadedIframe.style.left = '50%';
    preloadedIframe.style.transform = 'translate(-50%, -50%)';
    preloadedIframe.style.visibility = 'visible'; // Make visible
    preloadedIframe.style.zIndex = '1000000'; // Above backdrop (999999), below close button
    preloadedIframe.style.pointerEvents = 'auto'; // Enable interaction

    // Store reference
    this.iframeElement = preloadedIframe;

    console.log('✅ Using preloaded iframe for instant modal - repositioned to center!');
  }

  private createIframe(
    productData: ProductData, 
    _brandData: BrandData | null, 
    _trustCategories: TrustCategory[]
  ): void {
    console.log('🔧 Creating new iframe for modal');
    
    // Create iframe with same positioning as preloaded iframe
    this.iframeElement = document.createElement('iframe');
    this.iframeElement.className = 'supp-modal-iframe';
    this.iframeElement.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms allow-popups');
    this.iframeElement.setAttribute('loading', 'eager');
    
    // Position like preloaded iframe
    this.iframeElement.style.position = 'fixed';
    this.iframeElement.style.top = '50%';
    this.iframeElement.style.left = '50%';
    this.iframeElement.style.transform = 'translate(-50%, -50%)';
    this.iframeElement.style.width = '600px';
    this.iframeElement.style.height = this.getInitialIframeHeight();
    this.iframeElement.style.maxWidth = '90vw';
    this.iframeElement.style.maxHeight = '90vh';
    this.iframeElement.style.border = 'none';
    this.iframeElement.style.borderRadius = '8px';
    this.iframeElement.style.zIndex = '1000000'; // Above backdrop
    this.iframeElement.style.visibility = 'visible';
    this.iframeElement.style.pointerEvents = 'auto';

    // Build iframe URL with parameters
    const iframeUrl = this.buildIframeUrl(productData);
    console.log('🌐 Modal iframe URL:', iframeUrl);
    this.iframeElement.src = iframeUrl;

    // Append directly to body (like preloaded iframe)
    document.body.appendChild(this.iframeElement);

    // Handle iframe load errors
    this.iframeElement.addEventListener('error', () => {
      console.error('❌ New iframe failed to load');
      throw new Error('Iframe failed to load');
    });

    // Add load event for analytics
    this.iframeElement.addEventListener('load', () => {
      console.log('✅ New iframe loaded successfully:', iframeUrl);
    });
  }

  private buildIframeUrl(productData: ProductData): string {
    // Try to use slug first, fall back to UPC/identifier
    let baseUrl: string;
    
    if (productData?.product?.slug) {
      // Use slug-based URL pattern for iframe
      baseUrl = `${this.config.domain}/products/${productData.product.slug}/widget`;
    } else if (this.currentProductIdentifier) {
      // Fall back to UPC-based URL pattern
      baseUrl = `${this.config.domain}/products/upc/${this.currentProductIdentifier}/widget`;
    } else {
      console.warn('❌ Cannot build iframe URL - no product slug or identifier available');
      return '';
    }

    const url = new URL(baseUrl);

    // Add widget-specific parameters
    url.searchParams.set('widget_modal', 'true');
    url.searchParams.set('widget_version', this.config.version);
    url.searchParams.set('source_domain', window.location.hostname);
    url.searchParams.set('referrer', document.referrer || '');

    // Add session/analytics data
    const sessionId = this.getOrCreateSessionId();
    url.searchParams.set('session_id', sessionId);

    // Add cache-busting timestamp for iframe URLs
    url.searchParams.set('t', Date.now().toString());

    console.log('🔗 Built iframe URL:', url.toString());
    return url.toString();
  }

  private getOrCreateSessionId(): string {
    const key = 'supp_session_id';
    let sessionId = sessionStorage.getItem(key);
    
    if (!sessionId) {
      sessionId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      sessionStorage.setItem(key, sessionId);
    }
    
    return sessionId;
  }

  private getInitialIframeHeight(): string {
    // Calculate initial height for responsive iframe modal
    const viewportHeight = window.innerHeight;
    const maxModalHeight = Math.min(viewportHeight * 0.9, 860); // Max 90vh or 860px
    
    // Estimate content height based on typical modal content:
    // - Header: ~120px
    // - Trust categories: ~300px (5 categories * 60px each)
    // - Footer: ~60px
    // - Padding: ~40px
    const estimatedContentHeight = 860;
    
    // Use smaller of estimated content or max allowed
    const initialHeight = Math.min(estimatedContentHeight, maxModalHeight);
    
    return `${initialHeight}px`;
  }

  private showModal(): void {
    console.log('🎬 showModal() called');
    console.log('🎬 this.modalElement:', this.modalElement);
    
    if (!this.modalElement) {
      console.warn('❌ showModal() exiting - no modalElement');
      return;
    }

    console.log('🎬 Setting up iframe modal display');
    document.body.style.overflow = 'hidden';
    
    // Show backdrop with fade-in animation
    console.log('🎬 Adding modal backdrop to DOM');
    requestAnimationFrame(() => {
      console.log('🎬 Adding supp-modal-show class for fade-in');
      this.modalElement?.classList.add('supp-modal-show');
    });

    // Add ESC key handler for iframe modals
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.close();
        document.removeEventListener('keydown', escHandler);
      }
    };
    document.addEventListener('keydown', escHandler);
    console.log('🎬 ESC key handler added');

    // Focus management
    const focusableElements = this.modalElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusableElements.length > 0) {
      (focusableElements[0] as HTMLElement).focus();
    }
    console.log('🎬 showModal() completed');
  }

  public close(): void {
    console.log('🚪 close() called');
    if (!this.modalElement) return;

    // Handle iframe cleanup (both preloaded and newly created) first
    if (this.iframeElement && this.iframeElement.style.zIndex === '1000000') {
      // Check if this is a preloaded iframe (has data attribute) or new iframe
      if (this.iframeElement.hasAttribute('data-preloaded')) {
        // Move preloaded iframe completely off-screen and hide it
        this.iframeElement.style.position = 'fixed';
        this.iframeElement.style.top = '-9999px';
        this.iframeElement.style.left = '-9999px';
        this.iframeElement.style.transform = 'none';
        this.iframeElement.style.visibility = 'hidden';
        this.iframeElement.style.zIndex = '-1';
        this.iframeElement.style.pointerEvents = 'none';
        console.log('📦 Moved preloaded iframe off-screen for reuse');
      } else {
        // Remove newly created iframe
        this.iframeElement.remove();
        console.log('🗑️ Removed newly created iframe');
      }
      
      this.iframeElement = null;
    }

    // Smooth fade-out for iframe modals
    console.log('🚪 Closing iframe modal with fade-out');
    this.modalElement.classList.remove('supp-modal-show');
    
    setTimeout(() => {
      this.cleanup();
    }, 200); // Match CSS transition
  }

  private cleanup(): void {
    console.log('🧹 cleanup() called');
    
    // Remove modal backdrop/overlay
    if (this.modalElement) {
      this.modalElement.remove();
      this.modalElement = null;
      console.log('🧹 Modal element removed');
    }
    
    // Reset body overflow
    document.body.style.overflow = '';
    
    // Reset modal state
    this.currentProductIdentifier = undefined;
    this.trustCategories = [];
    
    console.log('🧹 Modal state reset to defaults');
  }

  private addIframeModalStyles(): void {
    const styleId = 'supp-iframe-modal-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .supp-modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.75);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 999999;
        opacity: 0;
        transition: opacity 0.2s ease;
        padding: 20px;
        box-sizing: border-box;
        backdrop-filter: blur(4px);
      }

      .supp-modal-overlay.supp-modal-show {
        opacity: 1;
      }

      .supp-modal-iframe {
        display: block;
        background: white;
        min-height: 400px;
        border-radius: 8px;
        box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
      }

      .supp-modal-close-overlay {
        position: fixed;
        top: calc(50% - 260px + 15px);
        right: calc(50% - 300px + 15px);
        z-index: 1000001;
        background: rgba(255, 255, 255, 0.9);
        border: none;
        border-radius: 50%;
        width: 32px;
        height: 32px;
        font-size: 18px;
        font-weight: bold;
        cursor: pointer;
        color: #374151;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        transition: all 0.2s ease;
      }

      .supp-modal-close-overlay:hover {
        background: rgba(255, 255, 255, 1);
        color: #111827;
        transform: scale(1.05);
      }

      @media (max-width: 768px) {
        .supp-modal-close-overlay {
          top: calc(50% - 200px + 10px);
          right: calc(50% - 200px + 10px);
        }
      }

      @media (max-width: 480px) {
        .supp-modal-close-overlay {
          top: calc(50% - 150px + 10px);
          right: calc(50% - 150px + 10px);
          width: 28px;
          height: 28px;
          font-size: 16px;
        }
      }
    `;

    document.head.appendChild(style);
  }
} 